import {
  ProjectFragment,
  ProjectInSidebarAndHeaderFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { TreeNodeData } from '../state.base/CollapsibleTree'
import {
  CurrentItemState,
  TreeSearch,
  TreeState,
} from '../state.base/TreeState'
import { treeSearchContext } from '../search/useTreeSearch'
import { debounce } from 'debounce'

export type TreeNodeTypes =
  | WorkspaceMinimalFragment
  // list of projects
  | ProjectInSidebarAndHeaderFragment
  // draft project
  | ProjectFragment

export type ProjectsTreeDataNode = TreeNodeData<TreeNodeTypes>

const treeStateContext = createContext<TreeState<TreeNodeTypes> | null>(null)

export interface TreeStateProviderProps extends PropsWithChildren<{}> {
  current: CurrentItemState
}

export const TreeStateProvider = (props: TreeStateProviderProps) => {
  const [search, setSearch] = useState<TreeSearch>({ name: '' })
  const [searchDebounced, _setSearchDebounced] = useState<TreeSearch>({
    name: '',
  })

  const setSearchDebounced = useMemo(
    () => debounce((value: TreeSearch) => _setSearchDebounced(value), 300),
    []
  )

  const onSearchChange = useCallback(
    (value: TreeSearch, immediate = false) => {
      setSearch(value)
      immediate ? _setSearchDebounced(value) : setSearchDebounced(value)
    },
    [setSearchDebounced]
  )

  const { children } = props

  const rootTreeState = useMemo(
    () => {
      const currentId =
        props.current.project?.identifier || props.current.workspace.identifier
      return new TreeState<TreeNodeTypes>(props.current, {
        focusedId: currentId,
        selectedId: currentId,
      })
    },

    // we are setting only the defaults, we DON'T need to re-create
    // the treeState once any of the props change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const searchTreeState = useMemo(
    () => {
      const currentId =
        props.current.project?.identifier || props.current.workspace.identifier
      return new TreeState<TreeNodeTypes>(props.current, {
        focusedId: currentId,
        selectedId: currentId,
      })
    },

    // we are setting only the defaults, we DON'T need to re-create
    // the treeState once any of the props change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchDebounced.name]
  )

  const propsRef = React.useRef(props)
  propsRef.current = props
  useEffect(() => {
    const current = propsRef.current.current
    if (current.originType === 'stashed') {
      // If the current item is stashed, preselect current location as the destination.
      // As for stashed items, we allow to transfer to the same location, this acts as a "restore" operation.
      const currentId =
        current.project?.identifier || current.workspace.identifier
      rootTreeState.onSelect(currentId)
    } else {
      rootTreeState.onSelect(null)
    }
  }, [searchDebounced.name, rootTreeState])

  /*
   * We want to separate tree states when we are in search mode and when we are not.
   * Thus we are able to preserve the tree state of full tree (e.g. open/close nodes)
   */
  const treeState =
    searchDebounced.name !== '' ? searchTreeState : rootTreeState

  return (
    <treeSearchContext.Provider
      value={{ search, onSearchChange, searchDebounced }}
    >
      <treeStateContext.Provider value={treeState}>
        {children}
      </treeStateContext.Provider>
    </treeSearchContext.Provider>
  )
}

export const useTreeState = () => {
  const treeState = useContext(treeStateContext)

  if (!treeState) {
    throw new Error('useTreeState must be used within a TreeStateProvider')
  }

  return treeState
}
